/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, DestroyRef, Inject, OnInit, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {BsModalRef} from 'ngx-bootstrap/modal';
import {catchError, finalize, of} from 'rxjs';
import * as _ from 'lodash';

import template from './ad-unit-bulk-action-modal.component.html';
import {createAdUnitCommons, resetDimensionsBasedOnAdUnitOption} from './ad-unit-commons';
import {createObjectTypeItemLink} from '../common/directives/adn-select/adn-select-item-link.util';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {AdUnitApiService, AdUnitTimeZone} from './ad-unit-api.service';
import {AdUnitSiteSearchState} from './ad-unit-search-state';

@Component({
  standalone: false,
  selector: 'adn-ad-unit-bulk-action-modal',
  template
})
export class AdUnitBulkActionModalComponent implements OnInit {
  list: any;

  item: any = {
    name: '{currentName}',
    description: '{currentDescription}',
    labelType: 'REPLACE',
    matchingLabelType: 'REPLACE',
    adUnitOption: 'RANGE',
    politicalAdvertising: 'ALLOWED',
    dimType: 'REPLACE',
    dimensions: [[]],
    allowedTargetTypes: []
  };
  form: any = {};
  editing: any = {};
  count = 0;
  showFullPageOptions = false;
  isPoliticalAdsPlatform = false;
  networkTimeZone = '';
  timeZones: AdUnitTimeZone[] | undefined;
  readonly timeZonesLoading = signal(true);
  readonly timeZonesLoadFailed = signal(false);
  specifyTimeZone = false;
  timeZoneChange?: () => void;
  targetingTypes: any[] = [];
  labelsResource: any;
  readonly siteSearchState = new AdUnitSiteSearchState(inject(AdUnitApiService), inject(DestroyRef));
  readonly siteItemLink = createObjectTypeItemLink('Site');

  private readonly commons: ReturnType<typeof createAdUnitCommons>;
  private readonly api = inject(AdUnitApiService);
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    public bsModalRef: BsModalRef,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(LocalUserProfile) localUserProfile: any
  ) {
    const isSpecialUser = localUserProfile.get('username') === 'adnuntius@adnuntius.com'
      || localUserProfile.get('username') === 'broker1@adnuntius.com';
    this.showFullPageOptions = LocalNetworkProfile.isPureMarketplacePlatform() && isSpecialUser;
    this.isPoliticalAdsPlatform = LocalNetworkProfile.isPoliticalAdsPlatform();
    this.networkTimeZone = LocalNetworkProfile.get('timeZone');
    this.commons = createAdUnitCommons(this, LocalNetworkProfile, 'item', this.item);
  }

  ngOnInit(): void {
    this.count = _.keys(this.list?.bulkSelection || {}).length;
    this.labelsResource = {item: this.item};
    this.api.listTimeZones().pipe(
      catchError(() => {
        this.timeZonesLoadFailed.set(true);
        return of([] as AdUnitTimeZone[]);
      }),
      finalize(() => this.timeZonesLoading.set(false)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((timeZones) => {
      this.timeZones = timeZones;
      this.specifyTimeZone = !!this.item.timeZone;
    });
    this.commons.setTimezoneChange();
    this.commons.setTargetingTypes();
  }

  removeDimensions(index: number): void {
    _.pullAt(this.item.dimensions, index);
  }

  addDimensions(): void {
    this.item.dimensions = this.item.dimensions || [];
    this.item.dimensions.push([]);
  }

  onTimeZoneModeChange(): void {
    if (this.timeZoneChange) {
      this.timeZoneChange();
    }
  }

  onPoliticalAdvertisingChange(blocked: boolean): void {
    this.item.politicalAdvertising = blocked ? 'BLOCKED' : 'ALLOWED';
  }

  apply(): void {
    const adUnits = _.map(this.list.bulkSelection, (v: any, k: string) => {
      const obj: any = {id: k};

      if (this.editing.name) {
        obj.name = this.item.name.replace('{currentName}', v.name);
      }
      if (this.editing.description) {
        obj.description = this.item.description.replace('{currentDescription}', v.description || '') || null;
      }
      if (this.item.adUnitOption === 'FIXED') {
        if (this.editing.dimensions) {
          const theDims = _.filter(this.item.dimensions, (dim: any[]) => {
            return _.isFinite(dim[0]) && dim[0] > 0 && _.isFinite(dim[1]) && dim[1] > 0;
          });
          obj.dimensions = this.item.dimType === 'APPEND' ? v.dimensions.concat(theDims || []) : theDims || [];
          obj.adUnitOption = 'FIXED';
          resetDimensionsBasedOnAdUnitOption(obj);
        }
      }
      if (this.item.adUnitOption === 'RANGE') {
        if (this.editing.minWidth) {
          obj.minWidth = this.item.minWidth;
          obj.minHeight = this.item.minHeight;
          obj.adUnitOption = 'RANGE';
          resetDimensionsBasedOnAdUnitOption(obj);
        }
        if (this.editing.maxWidth) {
          obj.width = this.item.width;
          obj.height = this.item.height;
          obj.adUnitOption = 'RANGE';
          resetDimensionsBasedOnAdUnitOption(obj);
        }
      }
      if (this.editing.pageSize) {
        obj.pageSize = this.item.pageSize || null;
      }
      if (this.editing.floorPrice) {
        obj.floorPrice = this.item.floorPrice || null;
      }
      if (this.editing.site) {
        obj.site = this.item.site || null;
      }
      if (this.editing.targetingControl && this.item.targetingControl) {
        obj.targetingControl = this.item.targetingControl || null;
        obj.allowedTargetTypes = this.item.allowedTargetTypes || [];
      }
      if (this.editing.matchingLabels) {
        obj.matchingLabels = this.item.matchingLabelType === 'APPEND'
          ? v.matchingLabels.concat(this.item.matchingLabels || [])
          : this.item.matchingLabels || [];
      }
      if (this.editing.labels) {
        obj.labels = this.item.labelType === 'APPEND'
          ? v.labels.concat(this.item.labels || [])
          : this.item.labels || [];
      }
      if (this.editing.timeZone) {
        obj.timeZone = this.commons.getBeforeSubmitTimeZoneValue();
      }
      if (this.editing.urlAsCategory) {
        obj.urlAsCategory = this.item.urlAsCategory;
        obj.qsAsKeyValues = this.item.qsAsKeyValues;
      }
      if (this.editing.isFullPage) {
        obj.isFullPage = this.item.isFullPage;
      }
      if (this.editing.politicalAdvertising) {
        obj.politicalAdvertising = this.item.politicalAdvertising;
      }

      return obj;
    });

    this.list.miscBulkAction(
      {close: () => this.bsModalRef.hide()},
      this,
      adUnits
    );
  }

  dismiss(): void {
    this.bsModalRef.hide();
  }
}
